/**
 * SVG backend — paints a laid-out scene as a portable SVG string.
 *
 * Output is real `<rect>`/`<text>`/nested `<svg>` (for glyphs/icons) — NO
 * `<foreignObject>`, so it opens in Illustrator/Inkscape/browsers and can
 * be generated entirely headless (no DOM). Gradient fills and drop shadows
 * emit `<linearGradient>` / `<filter>` defs.
 */
import type { LayoutBox, Rect } from "./layout.js";
import { LINE_HEIGHT } from "./metrics.js";
import type { Gradient, Shadow, TextNode, RichTextNode } from "./model.js";

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}

function rectAttrs(r: Rect): string {
  return `x="${round(r.x)}" y="${round(r.y)}" width="${round(r.w)}" height="${round(r.h)}"`;
}

/** CSS gradient angle (0deg = to top, clockwise) → objectBoundingBox endpoints. */
function gradientCoords(angle: number): { x1: number; y1: number; x2: number; y2: number } {
  const rad = (angle * Math.PI) / 180;
  const sx = Math.sin(rad);
  const cy = Math.cos(rad);
  return {
    x1: round(0.5 - 0.5 * sx),
    y1: round(0.5 + 0.5 * cy),
    x2: round(0.5 + 0.5 * sx),
    y2: round(0.5 - 0.5 * cy),
  };
}

/** Split `rgba(r,g,b,a)` / hex into a flood-color + flood-opacity pair. */
function floodParts(color: string): { color: string; opacity: number } {
  const m = color.match(/rgba?\(([^)]+)\)/i);
  if (m) {
    const parts = m[1]!.split(",").map((s) => s.trim());
    const [r, g, b] = parts;
    const a = parts[3] != null ? Number(parts[3]) : 1;
    return { color: `rgb(${r},${g},${b})`, opacity: a };
  }
  return { color, opacity: 1 };
}

interface Defs {
  readonly out: string[];
  n: number;
  /** Id prefix so defs from different scenes concatenated into one document
   *  (a board of many nodes) don't collide on `g0`/`c0`/`s0`. */
  readonly prefix: string;
}

function gradientDef(g: Gradient, defs: Defs): string {
  const id = `${defs.prefix}g${defs.n++}`;
  const { x1, y1, x2, y2 } = gradientCoords(g.angle);
  defs.out.push(
    `<linearGradient id="${id}" x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}">` +
      `<stop offset="0" stop-color="${g.from}"/>` +
      `<stop offset="1" stop-color="${g.to}"/>` +
      `</linearGradient>`,
  );
  return id;
}

function shadowDef(s: Shadow, defs: Defs): string {
  const id = `${defs.prefix}s${defs.n++}`;
  const { color, opacity } = floodParts(s.color);
  // Generous region so the blur isn't clipped by the default filter box.
  defs.out.push(
    `<filter id="${id}" x="-50%" y="-50%" width="200%" height="200%">` +
      `<feDropShadow dx="${round(s.dx)}" dy="${round(s.dy)}" stdDeviation="${round(s.blur / 2)}" ` +
      `flood-color="${color}" flood-opacity="${round(opacity)}"/>` +
      `</filter>`,
  );
  return id;
}

/** SVG path for a rect with per-corner radii [tl, tr, br, bl]. */
function roundedRectPath(r: Rect, [tl, tr, br, bl]: readonly [number, number, number, number]): string {
  const { x, y, w, h } = r;
  const R = round;
  return (
    `M${R(x + tl)},${R(y)} H${R(x + w - tr)} A${R(tr)},${R(tr)} 0 0 1 ${R(x + w)},${R(y + tr)} ` +
    `V${R(y + h - br)} A${R(br)},${R(br)} 0 0 1 ${R(x + w - br)},${R(y + h)} ` +
    `H${R(x + bl)} A${R(bl)},${R(bl)} 0 0 1 ${R(x)},${R(y + h - bl)} ` +
    `V${R(y + tl)} A${R(tl)},${R(tl)} 0 0 1 ${R(x + tl)},${R(y)} Z`
  );
}

function paintBox(lb: LayoutBox, out: string[], defs: Defs, ownOpacity: boolean): void {
  if (lb.node.type !== "box" || !lb.node.style) return;
  const s = lb.node.style;
  const hasPaint = s.fill || s.gradient || s.stroke;
  if (!hasPaint) return;
  const fill = s.gradient ? `url(#${gradientDef(s.gradient, defs)})` : (s.fill ?? "none");
  const paint: string[] = [`fill="${fill}"`];
  if (s.shadow) paint.push(`filter="url(#${shadowDef(s.shadow, defs)})"`);
  if (s.stroke) {
    paint.push(`stroke="${s.stroke}"`, `stroke-width="${s.strokeWidth ?? 1}"`);
    if (s.dashed) paint.push(`stroke-dasharray="4 3"`);
  }
  // Only when this shape is the box's whole rendering. A box with children
  // gets its opacity from the group `walk` wraps around it, and emitting it
  // here as well would multiply the fade into itself.
  if (ownOpacity && s.opacity != null && s.opacity < 1) paint.push(`opacity="${round(s.opacity)}"`);

  // Inset a stroked shape by half its width so the stroke stays inside the
  // box (SVG strokes straddle the path edge and would otherwise clip at the
  // node boundary). Fill-only shapes use the full rect.
  const inset = s.stroke ? (s.strokeWidth ?? 1) / 2 : 0;
  const r: Rect = inset
    ? { x: lb.rect.x + inset, y: lb.rect.y + inset, w: Math.max(0, lb.rect.w - 2 * inset), h: Math.max(0, lb.rect.h - 2 * inset) }
    : lb.rect;

  if (s.shape === "ellipse") {
    out.push(
      `<ellipse cx="${round(r.x + r.w / 2)}" cy="${round(r.y + r.h / 2)}" ` +
        `rx="${round(r.w / 2)}" ry="${round(r.h / 2)}" ${paint.join(" ")}/>`,
    );
    return;
  }
  if (Array.isArray(s.radius)) {
    // Per-corner radius — <rect> only does uniform rx, so emit a path.
    const radii = (s.radius as [number, number, number, number]).map((v) => Math.max(0, v - inset)) as [number, number, number, number];
    out.push(`<path d="${roundedRectPath(r, radii)}" ${paint.join(" ")}/>`);
    return;
  }
  const attrs = [rectAttrs(r)];
  if (s.radius) attrs.push(`rx="${round(Math.max(0, (s.radius as number) - inset))}"`);
  out.push(`<rect ${attrs.join(" ")} ${paint.join(" ")}/>`);
}

function paintText(lb: LayoutBox, out: string[]): void {
  const node = lb.node as TextNode;
  const { font, color, align = "start" } = node.style;
  const r = lb.rect;
  const anchor = align === "center" ? "middle" : align === "end" ? "end" : "start";
  const tx = align === "center" ? r.x + r.w / 2 : align === "end" ? r.x + r.w : r.x;
  const head =
    `font-family="${esc(font.family)}" font-size="${round(font.size)}" ` +
    `font-weight="${font.weight}" fill="${color}" ` +
    (node.style.strike ? `text-decoration="line-through" ` : "") +
    `text-anchor="${anchor}" dominant-baseline="central"`;

  const lines = lb.lines;
  if (lines && lines.length) {
    // Vertically center the block of lines within the allotted rect.
    const lh = font.size * LINE_HEIGHT;
    const top = r.y + (r.h - lines.length * lh) / 2 + lh / 2;
    for (let i = 0; i < lines.length; i++) {
      out.push(
        `<text x="${round(tx)}" y="${round(top + i * lh)}" ${head}>${esc(lines[i]!)}</text>`,
      );
    }
    return;
  }
  // Single line, vertically centered.
  const ty = r.y + r.h / 2;
  out.push(`<text x="${round(tx)}" y="${round(ty)}" ${head}>${esc(lb.displayText ?? node.text)}</text>`);
}

function paintRichText(lb: LayoutBox, out: string[]): void {
  const node = lb.node as RichTextNode;
  const { font, color } = node.style;
  const mono = node.style.monoFamily;
  const linkColor = node.style.linkColor ?? "#60a5fa";
  const codeColor = "#a5b4fc";
  const r = lb.rect;
  const lines = lb.richLines ?? [];
  const lh = font.size * LINE_HEIGHT;
  const codeBg = "rgba(148,163,184,0.16)";
  const align = node.style.align ?? "start";
  // Per-line left offset so a centered/right-aligned line sits in the box.
  const lineOffset = (spans: readonly { x?: number; w?: number }[]) => {
    if (align === "start" || !spans.length) return 0;
    const last = spans[spans.length - 1]!;
    const lineW = (last.x ?? 0) + (last.w ?? 0);
    return align === "center" ? (r.w - lineW) / 2 : r.w - lineW;
  };
  // Top-aligned paragraph (a body of prose, not a centered label).
  for (let i = 0; i < lines.length; i++) {
    const y = r.y + lh / 2 + i * lh;
    const spans = lines[i]!;
    const off = lineOffset(spans);
    // Pass 1: inline-code pill backgrounds, at each code span's measured x.
    for (const s of spans) {
      if (s.code && s.w != null) {
        out.push(
          `<rect x="${round(r.x + off + (s.x ?? 0) - 2)}" y="${round(y - lh * 0.42)}" ` +
            `width="${round(s.w + 4)}" height="${round(lh * 0.84)}" rx="3" fill="${codeBg}"/>`,
        );
      }
    }
    const spanAttrs = (s: typeof spans[number]) => {
      const a: string[] = [`font-weight="${s.bold ? Math.max(font.weight, 700) : font.weight}"`];
      if (s.italic) a.push(`font-style="italic"`);
      if (s.code && mono) a.push(`font-family="${esc(mono)}"`);
      const deco = [s.strike ? "line-through" : "", s.href ? "underline" : ""].filter(Boolean).join(" ");
      if (deco) a.push(`text-decoration="${deco}"`);
      a.push(`fill="${s.href ? linkColor : s.code ? codeColor : s.dim ? "#94a3b8" : color}"`);
      return a.join(" ");
    };

    // A line with an inline glyph (icon) is positioned by each span's measured
    // x so the icon sits in the flow; a plain text line uses flowing tspans
    // (renderer-spaced, robust to small metric differences).
    if (spans.some((s) => s.glyph)) {
      for (const s of spans) {
        const x = r.x + off + (s.x ?? 0);
        if (s.glyph) {
          const g = s.glyph;
          const col = g.color ? ` color="${g.color}"` : "";
          out.push(
            `<svg x="${round(x)}" y="${round(y - g.height / 2)}" width="${round(g.width)}" height="${round(g.height)}" ` +
              `viewBox="0 0 ${g.viewW} ${g.viewH}" preserveAspectRatio="xMidYMid meet" overflow="visible"${col}>${g.body}</svg>`,
          );
        } else {
          let el = `<text x="${round(x)}" y="${round(y)}" font-family="${esc(font.family)}" font-size="${round(font.size)}" ${spanAttrs(s)} text-anchor="start" dominant-baseline="central">${esc(s.text)}</text>`;
          if (s.href) el = `<a href="${esc(s.href)}">${el}</a>`;
          out.push(el);
        }
      }
      continue;
    }

    // Pass 2: the text itself as flowing tspans — the renderer lays out the
    // spacing, so it stays correct even when our metrics differ slightly.
    const tspans = spans
      .map((s, j) => {
        let tspan = `<tspan ${spanAttrs(s)}>${esc(s.text)}</tspan>`;
        if (s.href) tspan = `<a href="${esc(s.href)}">${tspan}</a>`;
        return `${j > 0 && s.spaceBefore !== false ? " " : ""}${tspan}`;
      })
      .join("");
    const anchor = align === "center" ? "middle" : align === "end" ? "end" : "start";
    const lx = align === "center" ? r.x + r.w / 2 : align === "end" ? r.x + r.w : r.x;
    out.push(
      `<text x="${round(lx)}" y="${round(y)}" font-family="${esc(font.family)}" ` +
        `font-size="${round(font.size)}" fill="${color}" text-anchor="${anchor}" ` +
        `dominant-baseline="central">${tspans}</text>`,
    );
  }
}

function paintImage(lb: LayoutBox, out: string[]): void {
  if (lb.node.type !== "image") return;
  // SVG2 `href`; fit into the box without distortion. A `<title>` carries alt
  // for accessibility + as a hover label in standalone viewers.
  out.push(
    `<image ${rectAttrs(lb.rect)} href="${esc(lb.node.src)}" ` +
      `preserveAspectRatio="xMidYMid meet">` +
      `<title>${esc(lb.node.alt)}</title></image>`,
  );
}

function paintGlyph(lb: LayoutBox, out: string[]): void {
  if (lb.node.type !== "glyph") return;
  const r = lb.rect;
  const color = lb.node.color ? ` color="${lb.node.color}"` : "";
  const op =
    lb.node.opacity != null && lb.node.opacity < 1
      ? ` opacity="${round(lb.node.opacity)}"`
      : "";
  // Inherited by every stroke in `body` — the only way to dash a shape the
  // glyph draws itself (cylinder, device figures).
  const dash = lb.node.dashed ? ` stroke-dasharray="4 3"` : "";
  out.push(
    `<svg ${rectAttrs(r)} viewBox="0 0 ${lb.node.viewW} ${lb.node.viewH}" ` +
      `preserveAspectRatio="xMidYMid meet" overflow="visible"${color}${op}${dash}>${lb.node.body}</svg>`,
  );
}

/** SVG markup for a box's outline at its full rect (for a clipPath). */
function clipShape(lb: LayoutBox): string {
  const s = (lb.node.type === "box" && lb.node.style) || undefined;
  const r = lb.rect;
  if (s && s.shape === "ellipse") {
    return `<ellipse cx="${round(r.x + r.w / 2)}" cy="${round(r.y + r.h / 2)}" rx="${round(r.w / 2)}" ry="${round(r.h / 2)}"/>`;
  }
  if (s && Array.isArray(s.radius)) {
    return `<path d="${roundedRectPath(r, s.radius as [number, number, number, number])}"/>`;
  }
  const rx = s && typeof s.radius === "number" ? ` rx="${round(s.radius)}"` : "";
  return `<rect ${rectAttrs(r)}${rx}/>`;
}

/** A box clips its content to its rounded/elliptical outline when it would
 *  otherwise overflow — overlays (stripe/badge), a collapsing band group, or
 *  an ellipse body. Mirrors the HTML backend's `overflow: hidden`. */
function boxClips(node: { overlays?: readonly unknown[]; growBand?: unknown; style?: { shape?: string } }): boolean {
  return !!(node.overlays?.length || node.growBand || node.style?.shape === "ellipse");
}

function walk(lb: LayoutBox, out: string[], defs: Defs): void {
  switch (lb.node.type) {
    case "box": {
      // A box's opacity fades the box AND everything inside it — the HTML
      // backend sets CSS `opacity`, which composites the whole subtree, so SVG
      // has to group rather than paint the shape alone. Left per-shape, a
      // ghosted node exported as a faint plate carrying a full-strength title
      // and description: a retired service read as an ordinary one.
      //
      // A CHILDLESS box needs no group — `<g opacity><rect/></g>` renders
      // exactly as `<rect opacity/>`, so the attribute stays on the shape and
      // leaf decoration (an accent bar, a divider) emits as before.
      const o = lb.node.style?.opacity;
      const faded = o != null && o < 1 && lb.children.length > 0;
      if (faded) out.push(`<g opacity="${round(o!)}">`);
      paintBox(lb, out, defs, !faded);
      if (boxClips(lb.node)) {
        const id = `${defs.prefix}c${defs.n++}`;
        defs.out.push(`<clipPath id="${id}">${clipShape(lb)}</clipPath>`);
        out.push(`<g clip-path="url(#${id})">`);
        for (const c of lb.children) walk(c, out, defs);
        out.push(`</g>`);
      } else {
        for (const c of lb.children) walk(c, out, defs);
      }
      if (faded) out.push(`</g>`);
      break;
    }
    case "text":
      paintText(lb, out);
      break;
    case "richtext":
      paintRichText(lb, out);
      break;
    case "glyph":
      paintGlyph(lb, out);
      break;
    case "image":
      paintImage(lb, out);
      break;
    case "spacer":
    case "slot":
      // Slots are host-filled portals; headless SVG renders them empty.
      break;
  }
}

export interface ToSvgOptions {
  /** Background fill behind the whole scene (omit for transparent). */
  readonly background?: string;
  /** Prefix for generated def ids (gradients/shadows/clips). Set a distinct
   *  value per node when concatenating many scenes into one document (a board)
   *  so `url(#c0)` references don't cross-resolve. Default "". */
  readonly idPrefix?: string;
}

export function toSVG(root: LayoutBox, opts: ToSvgOptions = {}): string {
  const { w, h } = root.rect;
  const body: string[] = [];
  const defs: Defs = { out: [], n: 0, prefix: opts.idPrefix ?? "" };
  if (opts.background) {
    body.push(`<rect x="0" y="0" width="${round(w)}" height="${round(h)}" fill="${opts.background}"/>`);
  }
  walk(root, body, defs);
  const defsBlock = defs.out.length ? `<defs>${defs.out.join("")}</defs>` : "";
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${round(w)}" height="${round(h)}" ` +
    `viewBox="0 0 ${round(w)} ${round(h)}">` +
    defsBlock +
    body.join("") +
    `</svg>`
  );
}
